import { useState, useEffect, useCallback } from 'react';

export const KANBAN_COLUMN_DEFAULT_WIDTH = 400;
export const KANBAN_COLUMN_MIN_WIDTH = 240;
export const KANBAN_COLUMN_MAX_WIDTH = 560;

function storageKey(projectId: string): string {
  return `pm:kanbanColWidths:${projectId}`;
}

function loadWidths(projectId: string): Record<string, number> {
  try {
    const raw = localStorage.getItem(storageKey(projectId));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return {};
    const out: Record<string, number> = {};
    for (const [id, w] of Object.entries(parsed as Record<string, unknown>)) {
      const n = Number(w);
      if (Number.isFinite(n)) {
        out[id] = Math.min(KANBAN_COLUMN_MAX_WIDTH, Math.max(KANBAN_COLUMN_MIN_WIDTH, Math.round(n)));
      }
    }
    return out;
  } catch {
    return {};
  }
}

function saveWidths(projectId: string, widths: Record<string, number>): void {
  try {
    localStorage.setItem(storageKey(projectId), JSON.stringify(widths));
  } catch {
    /* ignore */
  }
}

function clampWidth(width: number): number {
  return Math.min(KANBAN_COLUMN_MAX_WIDTH, Math.max(KANBAN_COLUMN_MIN_WIDTH, Math.round(width)));
}

export function useKanbanColumnWidths(projectId: string) {
  const [widths, setWidths] = useState<Record<string, number>>(() => loadWidths(projectId));

  useEffect(() => {
    setWidths(loadWidths(projectId));
  }, [projectId]);

  const getWidth = useCallback(
    (columnId: string) => widths[columnId] ?? KANBAN_COLUMN_DEFAULT_WIDTH,
    [widths],
  );

  const setWidth = useCallback(
    (columnId: string, width: number) => {
      const clamped = clampWidth(width);
      setWidths((prev) => {
        const next = { ...prev, [columnId]: clamped };
        saveWidths(projectId, next);
        return next;
      });
    },
    [projectId],
  );

  const resetWidth = useCallback(
    (columnId: string) => {
      setWidths((prev) => {
        const next = { ...prev };
        delete next[columnId];
        saveWidths(projectId, next);
        return next;
      });
    },
    [projectId],
  );

  return { getWidth, setWidth, resetWidth };
}
